import { useCallback, useEffect, useState } from 'react';

import { useAuthStore } from '@/stores/authStore';
import { useWebSocketStore } from '@/stores/websocketStore';

export function useAutoConnectWebSocket() {
  const token = useAuthStore((state) => state.token);
  const { connect, disconnect, isConnected, reconnect } = useWebSocketStore();
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  // Expose connection status to consuming components
  const connectionStatus = {
    isConnected,
    reconnecting,
    error: connectionError,
  };

  // Handle manual reconnection
  const attemptReconnect = useCallback(async () => {
    if (!token) return;

    setReconnecting(true);
    setConnectionError(null);

    try {
      await reconnect();
      setConnectionError(null);
    } catch (error) {
      setConnectionError(
        error instanceof Error ? error.message : 'Connection failed',
      );
    } finally {
      setReconnecting(false);
    }
  }, [token, reconnect]);

  useEffect(() => {
    let mounted = true;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    async function handleConnection() {
      if (!token || !mounted) {
        // No token means user is logged out, disconnect
        disconnect();
        return;
      }

      try {
        setReconnecting(false);
        setConnectionError(null);
        await connect(token);
      } catch (error) {
        if (mounted) {
          setConnectionError(
            error instanceof Error ? error.message : 'Connection failed',
          );

          // Setup reconnection with exponential backoff
          if (reconnectTimeout) clearTimeout(reconnectTimeout);
          reconnectTimeout = setTimeout(() => {
            if (mounted && token) {
              setReconnecting(true);
              handleConnection();
            }
          }, 3000); // Start with 3 seconds, could implement exponential backoff
        }
      }
    }

    handleConnection();

    // Clean up function
    return () => {
      mounted = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      disconnect();
    };
  }, [token, connect, disconnect]);

  return { ...connectionStatus, reconnect: attemptReconnect };
}
